import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { FEATURES, FEATURE_GROUPS, mediaUrl, sellerCenterPath, useOpenFeature } from './mainContent';

const CHIP_COLORS = ['#2F5BFF', '#6D3DF5'];

export const FeatureCard = ({ f, image, index }) => {
  const open = useOpenFeature();
  const Icon = f.icon;
  return (
    <button type="button" onClick={() => open(f)} id={`main-feature-${f.key}`} data-testid={`main-feature-card-${f.key}`}
      className="group text-left bg-white rounded-2xl border border-[#E4E7F6] p-2.5 md:p-4 shadow-[0_8px_24px_rgba(40,50,120,0.06)] hover:border-[#2F5BFF] hover:-translate-y-1 hover:shadow-[0_14px_32px_rgba(47,91,255,0.18)] transition-[transform,box-shadow,border-color] duration-300 main-fade-up flex flex-col min-w-0"
      style={{ animationDelay: `${index * 70}ms` }}>
      <div className="relative aspect-[16/10] rounded-xl overflow-hidden bg-gradient-to-br from-[#EEF1FF] to-[#F3ECFF] flex items-center justify-center">
        <span className="absolute top-2 left-2 md:top-3 md:left-3 z-10 inline-flex items-center justify-center w-7 h-7 md:w-9 md:h-9 rounded-full text-white shadow"
          style={{ background: CHIP_COLORS[index % 2] }}><Icon className="w-3.5 h-3.5 md:w-[18px] md:h-[18px]" /></span>
        {image ? (
          <img src={mediaUrl(image)} alt={f.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            data-testid={`main-feature-image-${f.key}`} />
        ) : (
          <Icon className="w-9 h-9 md:w-14 md:h-14 text-[#5B6BF5]/70" strokeWidth={1.6} data-testid={`main-feature-icon-${f.key}`} />
        )}
      </div>
      <div className="mt-2.5 md:mt-4 flex items-end justify-between gap-2 md:gap-3 px-0.5 md:px-1">
        <div className="min-w-0">
          <h3 className="text-[13px] md:text-[19px] font-bold text-[#1B2140] leading-snug break-keep">{f.title}</h3>
          <p className="hidden md:block mt-1 text-[14px] text-[#6B7194] leading-snug break-keep">{f.desc}</p>
        </div>
        <ArrowRight className="hidden md:block shrink-0 w-5 h-5 text-[#2F5BFF] group-hover:translate-x-1 transition-transform" />
      </div>
    </button>
  );
};

export const MainFeatures = ({ content }) => (
  <section className="relative rounded-t-[28px] md:rounded-t-[44px] bg-[#F6F7FF] px-4 md:px-12 pt-8 md:pt-16 pb-12 md:pb-20" data-testid="main-features">
    <div className="max-w-[1340px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 md:mb-10">
        <h2 className="text-[28px] md:text-[44px] font-black tracking-tight text-[#1B2140]">
          LIVEMOA <span className="bg-gradient-to-r from-[#2F5BFF] to-[#7B3DF5] bg-clip-text text-transparent">주요기능</span>
        </h2>
        <p className="text-[14px] md:text-[17px] text-[#5B6185] leading-relaxed">
          라이브 커머스에 필요한 핵심 기능을 모두 담아<br className="hidden md:block" /> 하나의 플랫폼에서 제공하는 올인원 솔루션입니다.
        </p>
      </div>
      {FEATURE_GROUPS.map((g) => (
        <div key={g.key} className="mb-8 md:mb-12 last:mb-0" data-testid={`main-features-group-${g.key}`}>
          <h3 className="mb-3 md:mb-5 text-[16px] md:text-[22px] font-bold text-[#2F45E0]">{g.label}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-6" data-testid={`main-features-grid-${g.key}`}>
            {FEATURES.filter((f) => f.group === g.key).map((f, i) => (
              <FeatureCard key={f.key} f={f} index={i} image={content?.card_images?.[f.key]} />
            ))}
          </div>
        </div>
      ))}
    </div>
  </section>
);

export const MainSellerStart = ({ user }) => (
  <section className="bg-white px-4 md:px-12 py-12 md:py-20" data-testid="main-seller-start">
    <div className="max-w-[1340px] mx-auto rounded-3xl bg-gradient-to-r from-[#1E5BFF] to-[#6D3DF5] px-6 md:px-14 py-10 md:py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
      <div>
        <h2 className="text-white text-[24px] md:text-[36px] font-black tracking-tight">판매자센터 시작하기</h2>
        <p className="mt-2 text-white/85 text-[14px] md:text-[17px]">판매자 가입 신청 후 LIVEMOA의 방송·주문 기능을 바로 이용해 보세요.</p>
      </div>
      <div className="flex flex-col min-[400px]:flex-row gap-3">
        <Link to="/seller/apply" data-testid="main-start-apply-btn"
          className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-white text-[#2F45E0] font-bold hover:bg-white/90 transition-colors">
          지금 무료로 시작하기 <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to={sellerCenterPath(user)} data-testid="main-start-seller-center-btn"
          className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl border border-white/70 text-white font-bold hover:bg-white/10 transition-colors">
          판매자센터 <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);
